import {useRouter} from "next/router"
import React, {
	CSSProperties,
	ElementType,
	ReactNode,
	forwardRef,
	memo,
} from "react"

import useProcessedContent from "../hooks/useProcessedContent"

import styles from "./Typography.module.scss"

export enum Size {
	XXXL = "XXXL",
	XXL = "XXL",
	XL = "XL",
	L = "L",
	M = "M",
	S = "S",
	XS = "XS",
	XXS = "XXS",
	XXXS = "XXXS",
}

export enum Variant {
	Title = "title",
	Paragraph = "paragraph",
	Description = "description",
	SectionTitle = "sectionTitle",
	SectionSubtitle = "sectoinSubtitle",
}

interface OwnProps {
	variant?: Variant
	size?: Size
	fontWeight?: CSSProperties["fontWeight"]
	center?: boolean
	noPadding?: boolean
	stripHtml?: boolean
	translate?: boolean
	truncate?: number
	contrast?: boolean
	content?: string | ReactNode
	children?: ReactNode
}

export type TypographyProps<C extends ElementType = any> = OwnProps & {
	as?: C
} & Omit<React.ComponentPropsWithoutRef<C>, keyof OwnProps | "as">

const SIZE_CLASS: Record<Size, string> = {
	[Size.XXL]: styles["size--XXL"],
	[Size.XXXL]: styles["size--XXXL"],
	[Size.XL]: styles["size--XL"],
	[Size.L]: styles["size--L"],
	[Size.M]: styles["size--M"],
	[Size.S]: styles["size--S"],
	[Size.XS]: styles["size--XS"],
	[Size.XXS]: styles["size--XXS"],
	[Size.XXXS]: styles["size--XXXS"],
}

const VARIANT_CLASS: Record<Variant, string> = {
	[Variant.Title]: styles["variant--title"],
	[Variant.Paragraph]: styles["variant--paragraph"],
	[Variant.Description]: styles["variant--description"],
	[Variant.SectionTitle]: styles["variant--sectionTitle"],
	[Variant.SectionSubtitle]: styles["variant--sectionSubtitle"],
}

const getVariantDefaults = <C extends ElementType>(
	variant: Variant,
): Partial<OwnProps & {as: C}> => {
	switch (variant) {
		case Variant.Title:
			return {
				as: "h2" as C,
				size: Size.XL,
				fontWeight: 600,
				translate: true,
			}
		case Variant.Description:
			return {
				as: "p" as C,
				size: Size.XS,
				fontWeight: 300,
				translate: true,
			}
		case Variant.SectionTitle:
			return {
				as: "h2" as C,
				size: Size.XL,
				fontWeight: 600,
				translate: true,
				noPadding: true,
			}
		case Variant.SectionSubtitle:
			return {
				as: "p" as C,
				size: Size.XS,
				fontWeight: 300,
				translate: true,
				noPadding: true,
			}
		case Variant.Paragraph:
			return {
				as: "p" as C,
				size: Size.M,
				fontWeight: 300,
				translate: true,
			}
		default:
			return {
				as: "p" as C,
				size: Size.S,
				fontWeight: 400,
				translate: true,
			}
	}
}

const Typography = forwardRef(
	<C extends ElementType>(props: TypographyProps<C>, ref: React.Ref<any>) => {
		const {
			as: asProp,
			variant = Variant.Paragraph,
			size: sizeProp,
			fontWeight: fwProp,
			center = false,
			noPadding,
			stripHtml = false,
			translate = false,
			contrast = false,
			truncate,
			content,
			children,
			className,
			style,
			...rest
		} = props

		const defs = getVariantDefaults<C>(variant)

		// Determine inner first
		const opts = {
			stripHtml,
			translate: translate ?? defs.translate,
			truncate,
		}
		const inner = children ?? useProcessedContent(content, opts)
		if (!inner) return null

		// Resolve tag and auto-fallback to div if inner isn’t plain text (prevents <p> inside <p>)
		const defaultTag = (asProp ?? defs.as ?? "p") as any
		const isPlainText =
			typeof inner === "string" || typeof inner === "number"
		const Tag =
			defaultTag === "p" && !isPlainText ? ("div" as any) : defaultTag

		const size = sizeProp ?? defs.size ?? Size.M
		const fontWeight = fwProp ?? defs.fontWeight ?? "400"

		const cls = [
			styles.typography,
			VARIANT_CLASS[variant],
			SIZE_CLASS[size],
			center && styles["align--center"],
			contrast && styles["color--contrast"],
			className,
		]
			.filter(Boolean)
			.join(" ")

		const css: CSSProperties = {
			textAlign: center ? "center" : undefined,
			fontWeight,
			paddingBottom: noPadding ?? defs.noPadding ? 0 : undefined,
			...style,
		}

		const router = useRouter()

		// Normalize locale for hyphenation dictionaries
		const normalizeLanguageCode = (lang?: string) => {
			if (!lang) return undefined
			const langLowerCase = lang.toLowerCase()
			if (langLowerCase === "se" || langLowerCase === "sv-se") return "sv"
			if (langLowerCase === "no" || langLowerCase === "no-no") return "nb"

			return langLowerCase.split("-")[0]
		}

		const {lang: langProp, ...domProps} = rest as any
		const lang = normalizeLanguageCode(langProp ?? router.locale)

		return (
			<Tag
				ref={ref}
				className={cls}
				style={css}
				// {...(rest as any)}
				{...domProps}
				lang={lang}
				key={lang}
			>
				{inner}
			</Tag>
		)
	},
)

type TypographyComponent = <C extends ElementType>(
	props: TypographyProps<C> & {ref?: React.Ref<any>},
) => React.ReactElement | null

export default memo(Typography) as TypographyComponent
